import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Loader2, 
  Globe,
  RefreshCw,
  GraduationCap,
  Briefcase,
  TrendingUp,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';

const API_URL = window.location.origin;

const AdminDestinationCompare = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [formData, setFormData] = useState({
    country_code: '',
    country_name: '',
    flag: '',
    tuition: '',
    living_cost: '',
    work_visa: '',
    pr_pathway: '',
    pr_difficulty: 'Moderate',
    avg_salary: '',
    top_for: '',
    highlight: '',
    seo_description: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/destination-compare/admin/all`);
      if (response.ok) {
        const data = await response.json();
        setDestinations(data);
      }
    } catch (error) {
      console.error('Error fetching destinations:', error);
      toast.error('Failed to load destinations');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will replace all existing destination comparison data with seed data. Continue?')) {
      return;
    }
    
    setSeeding(true);
    try {
      const response = await fetch(`${API_URL}/api/destination-compare/admin/seed`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Seed data loaded successfully!');
        fetchDestinations();
      } else {
        toast.error('Failed to seed data');
      }
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Failed to seed data');
    } finally {
      setSeeding(false);
    }
  };

  const resetForm = () => {
    setFormData({
      country_code: '',
      country_name: '',
      flag: '',
      tuition: '',
      living_cost: '',
      work_visa: '',
      pr_pathway: '',
      pr_difficulty: 'Moderate',
      avg_salary: '',
      top_for: '',
      highlight: '',
      seo_description: '',
      display_order: 0,
      is_active: true
    });
    setEditingDestination(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (destination) => {
    setEditingDestination(destination);
    setFormData({
      country_code: destination.country_code,
      country_name: destination.country_name,
      flag: destination.flag,
      tuition: destination.tuition,
      living_cost: destination.living_cost,
      work_visa: destination.work_visa,
      pr_pathway: destination.pr_pathway,
      pr_difficulty: destination.pr_difficulty,
      avg_salary: destination.avg_salary,
      top_for: destination.top_for?.join(', ') || '',
      highlight: destination.highlight,
      seo_description: destination.seo_description || '',
      display_order: destination.display_order || 0,
      is_active: destination.is_active
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...formData,
      top_for: formData.top_for.split(',').map(s => s.trim()).filter(Boolean)
    };

    try {
      let response;
      if (editingDestination) {
        response = await fetch(`${API_URL}/api/destination-compare/admin/${editingDestination.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${API_URL}/api/destination-compare/admin/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingDestination ? 'Destination updated!' : 'Destination created!');
        setIsDialogOpen(false);
        resetForm();
        fetchDestinations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save destination');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this destination?')) return;

    try {
      const response = await fetch(`${API_URL}/api/destination-compare/admin/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Destination deleted');
        fetchDestinations();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete destination');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Destination Comparisons</h1>
          <p className="text-gray-600">Manage country comparison data shown on destination pages</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSeedData}
            disabled={seeding}
          >
            {seeding ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Database className="h-4 w-4 mr-2" />
            )}
            Seed Data
          </Button>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Add Destination
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Globe className="h-8 w-8 text-ux-cyan" />
              <div>
                <p className="text-2xl font-bold">{destinations.length}</p>
                <p className="text-sm text-gray-600">Total Destinations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-8 w-8 text-ux-green" />
              <div>
                <p className="text-2xl font-bold">{destinations.filter(d => d.is_active).length}</p>
                <p className="text-sm text-gray-600">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Destinations Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Destinations</CardTitle>
        </CardHeader>
        <CardContent>
          {destinations.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">No destinations yet. Seed data or add your first destination.</p>
              <Button onClick={handleSeedData} disabled={seeding}>
                {seeding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                Load SEO Seed Data
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Country</TableHead>
                  <TableHead>Tuition</TableHead>
                  <TableHead>Work Visa</TableHead>
                  <TableHead>Highlight</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {destinations.map((dest) => (
                  <TableRow key={dest.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{dest.flag}</span>
                        <div>
                          <p className="font-medium">{dest.country_name}</p>
                          <p className="text-xs text-gray-500">{dest.country_code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{dest.tuition}</TableCell>
                    <TableCell>{dest.work_visa}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{dest.highlight}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={dest.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {dest.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>{dest.display_order}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(dest)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(dest.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDestination ? 'Edit Destination' : 'Add New Destination'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Country Code *</Label>
                <Input 
                  value={formData.country_code}
                  onChange={(e) => setFormData({...formData, country_code: e.target.value})}
                  placeholder="e.g., usa, canada, uk"
                  required
                />
              </div>
              <div>
                <Label>Country Name *</Label>
                <Input 
                  value={formData.country_name}
                  onChange={(e) => setFormData({...formData, country_name: e.target.value})}
                  placeholder="e.g., USA, Canada, UK"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Flag Emoji *</Label>
                <Input 
                  value={formData.flag}
                  onChange={(e) => setFormData({...formData, flag: e.target.value})}
                  placeholder="🇺🇸"
                  required
                />
              </div>
              <div>
                <Label>Highlight (USP) *</Label>
                <Input 
                  value={formData.highlight}
                  onChange={(e) => setFormData({...formData, highlight: e.target.value})}
                  placeholder="e.g., Highest salaries"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Avg. Tuition/Year *</Label>
                <Input 
                  value={formData.tuition}
                  onChange={(e) => setFormData({...formData, tuition: e.target.value})}
                  placeholder="e.g., $20K - $60K"
                  required
                />
              </div>
              <div>
                <Label>Living Cost/Year *</Label>
                <Input 
                  value={formData.living_cost}
                  onChange={(e) => setFormData({...formData, living_cost: e.target.value})}
                  placeholder="e.g., $15K - $25K"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Post-Study Work Visa *</Label>
                <Input 
                  value={formData.work_visa}
                  onChange={(e) => setFormData({...formData, work_visa: e.target.value})}
                  placeholder="e.g., 1-3 yrs (OPT)"
                  required
                />
              </div>
              <div>
                <Label>PR Pathway *</Label>
                <Input 
                  value={formData.pr_pathway}
                  onChange={(e) => setFormData({...formData, pr_pathway: e.target.value})}
                  placeholder="e.g., H-1B → Green Card"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PR Difficulty *</Label>
                <select
                  className="w-full h-10 px-3 border rounded-md"
                  value={formData.pr_difficulty}
                  onChange={(e) => setFormData({...formData, pr_difficulty: e.target.value})}
                >
                  <option value="Easy">Easy</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
              <div>
                <Label>Avg. Starting Salary *</Label>
                <Input 
                  value={formData.avg_salary}
                  onChange={(e) => setFormData({...formData, avg_salary: e.target.value})}
                  placeholder="e.g., $80K - $120K"
                  required
                />
              </div>
            </div>

            <div>
              <Label>Best For (comma-separated) *</Label>
              <Input 
                value={formData.top_for}
                onChange={(e) => setFormData({...formData, top_for: e.target.value})}
                placeholder="e.g., Tech, Research, MBA"
                required
              />
            </div>

            <div>
              <Label>SEO Description</Label>
              <Textarea 
                value={formData.seo_description}
                onChange={(e) => setFormData({...formData, seo_description: e.target.value})}
                placeholder="Meta description for SEO (150-160 chars)"
                rows={3}
              />
            </div>

            <div>
              <Label>Display Order</Label>
              <Input 
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
              />
            </div>

            <PublishStatusCard
              isPublished={formData.is_active}
              onChange={(checked) => setFormData({...formData, is_active: checked})}
              entityLabel="destination"
              publicRoute="/destinations"
              testId="destination-compare-publish-toggle"
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingDestination ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminDestinationCompare;
